import {type MouseEvent, useCallback} from 'react'

import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useNavigateToComment, usePullRequestBasePath} from '../stores/layout-selectors'
import {addUrlToHistoryStack} from '@github-ui/history'
import {useNavigate} from '@github-ui/use-navigate'

/**
 * Appends `new_files_changed=true` to an outdated comment's source-diff URL. Keeps
 * navigation to the original diff in the new Files changed experience rather than
 * falling back to the classic experience (matches `ActivityHeader.getOutdatedCommentUrl`).
 */
export function withNewFilesChanged(sourceUri: string): string {
  // `originalDiffPathUri` may be absolute or root-relative (e.g. `/owner/repo/pull/1/files/...`),
  // so resolve against the current origin (`new URL(relativePath)` alone throws, which would
  // crash the panel while rendering an outdated row).
  const url = new URL(sourceUri, ssrSafeLocation.origin)
  url.searchParams.set('new_files_changed', 'true')
  return url.toString()
}

type UseCommentNavigationArgs = {
  /** Database id of the comment to navigate to; a null/undefined id disables navigation. */
  commentId: number | null | undefined
  /** Whether the source thread is outdated; its diff may be absent from the current view. */
  isOutdated: boolean
  /** The thread's `originalDiffPathUri`, used to reach an outdated comment's original diff. */
  sourceUri: string | null | undefined
  /** Optional callback run after an in-app navigation (e.g. to scroll the target into view). */
  onNavigated?: () => void
}

/**
 * Shared navigation for "jump to a review comment" affordances (Manage batch panel
 * rows, thread previews). Returns the anchor `href` plus a click handler that:
 *
 * - lets modifier / middle clicks fall through to the native `href` (new tab, copy link);
 * - routes an outdated comment to its original diff (`sourceUri` + `new_files_changed`),
 *   since the current `/changes` view may not contain the old hunk;
 * - otherwise uses the registered files-changed `navigateToComment` callback when present,
 *   falling back to a plain `/changes#r<id>` navigation.
 *
 * Callers that render their own href (e.g. a bare `#r<id>` hash when already on the
 * files-changed route) can ignore the returned `href` and wire only `onClick`.
 */
export function useCommentNavigation({commentId, isOutdated, sourceUri, onNavigated}: UseCommentNavigationArgs) {
  const navigateToComment = useNavigateToComment()
  const basePath = usePullRequestBasePath()
  const navigate = useNavigate()

  // TODO(#956): A recovered item can be `isOutdated` with no `sourceUri` (marked outdated
  // after a head change though its thread was current at add time), so it falls back to the
  // current `/changes` view, which may no longer contain the old hunk. Persist a durable
  // comment URL and use it here: https://github.com/github/copilot-discovery-team/issues/956
  const href =
    isOutdated && sourceUri
      ? withNewFilesChanged(sourceUri)
      : commentId != null
        ? `${basePath}/changes#r${commentId}`
        : undefined

  const onClick = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      if (commentId == null) return
      // Let modifier / middle clicks open the anchor's href natively.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- handle locally unless the user is opening in a new tab/window
      if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) return
      event?.preventDefault()

      if (isOutdated && sourceUri) {
        window.location.href = withNewFilesChanged(sourceUri)
      } else if (navigateToComment) {
        navigateToComment(commentId)
        const currentUrl = window.location.pathname + window.location.search
        addUrlToHistoryStack(`${currentUrl}#r${commentId}`)
        onNavigated?.()
      } else {
        navigate(`${basePath}/changes#r${commentId}`)
        onNavigated?.()
      }
    },
    [commentId, isOutdated, sourceUri, navigateToComment, navigate, basePath, onNavigated],
  )

  return {href, onClick}
}
